class CustomBuilder {
  constructor() {
    this.data = window.customBuilderData || { products: [] };
    this.selectedProduct = null;
    this.selectedVariant = null;
    this.panel = document.getElementById('custom-customizer-panel');
    this.content = document.getElementById('custom-customizer-content');
    this.grid = document.getElementById('custom-product-picker');
    this.backButton = document.getElementById('custom-customizer-back');
    this.fallback = window.customBuilderFallback || {};
    this.strings = this.data.strings || {};
    this.currentForm = null;
    this.activeForm = null;
    this.boundFormChange = this.handleFormChange.bind(this);
    this.boundFormSubmit = this.handleFormSubmit.bind(this);
    this.boundBackClick = this.handleBackClick.bind(this);
    this.requiresBeadSelection = false;
    this.charmPresetLimits = null;
    this.charmSelection = null;
    this.currentRules = null;
    this.currentBeadTypes = [];
    this.currentBeadImages = [];
    this.currentBeadInventory = {};
    this.draggedCharm = null;
    this.dropzoneTimers = {};
    this.colorStatusTimer = null;
    this.init();
  }

  init() {
    this.toggleProductGrid(true);
    this.setupProductCards();
    if (this.backButton) {
      this.backButton.addEventListener('click', this.boundBackClick);
    }
    this.handleInitialSelection();
  }

  setupProductCards() {
    const cards = document.querySelectorAll('.custom-card');
    cards.forEach(card => {
      card.setAttribute('aria-pressed', 'false');
      card.addEventListener('click', () => {
        this.handleCardClick(card);
      });
      card.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleCardClick(card);
        }
      });
    });
  }

  handleCardClick(card) {
    const handle = card.dataset.productHandle;
    if (handle) {
      this.selectProduct(handle);
    }
  }

  handleInitialSelection() {
    if (!this.data.products.length) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const productHandle = urlParams.get('product');

    if (productHandle) {
      const match = this.data.products.find(product => product.handle === productHandle);
      if (match) {
        this.selectProduct(productHandle, false);
        return;
      }
    }

    this.clearSelectionState();
  }

  selectProduct(handle, updateUrl = true) {
    const product = this.data.products.find(item => item.handle === handle);
    if (!product) {
      return;
    }

    this.applyFallback(product);

    const firstAvailable = product.variants.find(variant => variant.available) || product.variants[0] || null;

    this.selectedProduct = product;
    this.selectedVariant = firstAvailable;

    this.updateCardSelection(handle);

    if (updateUrl) {
      this.updateUrl(handle);
    }

    this.toggleProductGrid(false);
    this.showLoadingState();
    this.renderCustomizer();
    this.scrollToCustomizer();
  }

  updateCardSelection(handle) {
    const cards = document.querySelectorAll('.custom-card');
    cards.forEach(card => {
      if (card.dataset.productHandle === handle) {
        card.classList.add('selected');
        card.setAttribute('aria-pressed', 'true');
      } else {
        card.classList.remove('selected');
        card.setAttribute('aria-pressed', 'false');
      }
    });
  }

  updateUrl(handle) {
    const url = new URL(window.location);
    if (handle) {
      url.searchParams.set('product', handle);
    } else {
      url.searchParams.delete('product');
    }
    window.history.pushState({}, '', url);
  }

  async renderCustomizer() {
    if (!this.panel || !this.content || !this.selectedProduct) {
      return;
    }

    const targetHandle = this.selectedProduct.handle;
    let fetchedContent = '';
    let fetched = false;

    if (this.selectedProduct.url) {
      try {
        const response = await fetch(`${this.selectedProduct.url}?view=custom-customizer`);
        if (response.ok) {
          fetchedContent = await response.text();
          fetched = true;
        }
      } catch (error) {
        fetched = false;
      }
    }

    if (!fetched) {
      fetchedContent = this.buildCustomizerHTML();
    }

    if (!fetchedContent || fetchedContent.indexOf('custom-product-form') === -1) {
      fetchedContent = this.buildCustomizerHTML();
    }

    if (!this.selectedProduct || this.selectedProduct.handle !== targetHandle) {
      return;
    }

    this.content.innerHTML = fetchedContent;
    this.injectBeadGallery(this.selectedProduct);
    if (!this.data.enableDynamicCheckout) {
      const paymentButton = this.content.querySelector('.shopify-payment-button');
      if (paymentButton && paymentButton.parentElement) {
        paymentButton.parentElement.removeChild(paymentButton);
      }
    }
    this.activeForm = this.content.querySelector('.custom-product-form');
    this.syncVariantId();
    this.panel.classList.add('active');
    this.setupCustomizerListeners();
    this.initializeCharmState();
    this.handleVariantChange();
    this.updatePrice();
    this.toggleProductGrid(false);
  }

  buildCustomizerHTML() {
    const product = this.selectedProduct;
    if (!product) {
      return '';
    }

    const variant = this.selectedVariant || product.variants[0] || {};
    const price = this.formatMoney(variant.price || product.price || 0);
    const materialsSource = product.metafields && product.metafields.materials;
    const presetsSource = product.metafields && product.metafields.presets;
    const beadTypesSource = product.metafields && product.metafields.beadTypes;
    const beadImagesSource = product.metafields && product.metafields.beadImages;
    const beadSizesSource = product.metafields && product.metafields.beadSizes;
    const beadInventorySource = product.metafields && product.metafields.beadInventory;
    const materials = Array.isArray(materialsSource) ? materialsSource : [];
    const presets = Array.isArray(presetsSource) ? presetsSource : [];
    const beadTypes = Array.isArray(beadTypesSource) ? beadTypesSource : [];
    const beadImages = Array.isArray(beadImagesSource) ? beadImagesSource : [];
    const beadSizes = Array.isArray(beadSizesSource) ? beadSizesSource : [];
    this.currentBeadInventory = this.parseBeadInventory(beadInventorySource);
    const galleryImages = Array.isArray(product.images) ? product.images.filter(Boolean).slice(0, 6) : [];
    const mainImageSrc = product.featuredImage || galleryImages[0] || '';
    const rules = this.parseRules(product.metafields ? product.metafields.rulesJson : null);

    const mediaSection = product.featuredImage || galleryImages.length
      ? `
        <div class="custom-media-column">
          <div class="product__media-wrapper">
            <div id="custom-media-gallery-${product.id}">
              ${mainImageSrc ? `
                <div class="product__media-item" style="margin-bottom: 1rem;">
                  <img
                    src="${mainImageSrc}"
                    alt="${product.title}"
                    width="800"
                    class="custom-main-image"
                    style="width: 100%; height: auto; border-radius: 0.5rem;"
                  >
                </div>
              ` : ''}
              ${galleryImages.length > 1 ? `
                <div class="product__media-thumbnails" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 0.5rem;">
                  ${galleryImages.map(image => `
                    <button type="button" class="product__media-thumbnail" style="border: 2px solid transparent; border-radius: 0.25rem; overflow: hidden; cursor: pointer; padding: 0; background: none;">
                      <img src="${image}" data-full-src="${image}" alt="${product.title}" loading="lazy" style="width: 100%; height: auto; display: block;">
                    </button>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `
      : '';

    return `
      <div class="custom-two-column" id="custom-product-${product.id}">
        ${mediaSection}
        <div class="custom-form-column">
          <div class="product__info-container">
            <h2 class="h1" style="margin-bottom: 0.5rem;">${product.title}</h2>
            <div class="price-wrapper" style="margin-bottom: 1rem;">
              <span class="price price--large" id="custom-price-${product.id}">${price}</span>
              <p class="caption" style="margin-top: 0.25rem;">${this.strings.taxesIncluded || 'Taxes included'}</p>
            </div>
            <form class="custom-product-form" data-product-id="${product.id}">
              <input type="hidden" name="id" value="${variant.id || ''}" class="custom-variant-id">
              ${this.buildOptionsHTML(product, variant)}
              ${this.buildMetafieldColors(product)}
              ${this.buildMaterialsHTML(materials)}
              ${this.buildCustomizationNoteHTML(product)}
              ${this.buildPresetsHTML(presets, product, rules)}
              ${this.buildBeadGalleryHTML(beadTypes, beadImages, beadSizes)}
              <div class="custom-field">
                <label for="custom-quantity-${product.id}">${this.strings.quantity || 'Quantity'}</label>
                <input
                  type="number"
                  id="custom-quantity-${product.id}"
                  name="quantity"
                  value="1"
                  min="1"
                  class="custom-quantity-input"
                >
              </div>
              <div class="custom-actions" style="margin-top: 2rem;">
                <button type="submit" class="button button--full-width button--primary" ${variant.available === false ? 'disabled' : ''}>
                  <span>${variant.available === false ? (this.strings.soldOut || 'Sold out') : (this.strings.addToCart || 'Add to cart')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
  }

  buildOptionsHTML(product, variant) {
    if (!Array.isArray(product.options) || !product.options.length) {
      return '';
    }

    return product.options.map(option => {
      const variantOptions = Array.isArray(variant.options) ? variant.options : [];
      const selectedValue = variantOptions[option.position - 1] || option.values[0];

      if (option.name && option.name.toLowerCase().includes('color')) {
        return `
          <div class="custom-field">
            <label>${this.getColorLabel(option.position)}</label>
            <div class="custom-color-swatches">
              ${option.values.map((value, index) => {
                const info = this.normalizeColorToken(value);
                const isSelected = value === selectedValue;
                return `
                  <input
                    type="radio"
                    id="custom-${product.id}-${option.position}-${index}"
                    name="options[${option.name}]"
                    value="${this.escapeHtml(value)}"
                    class="visually-hidden custom-option-input"
                    data-option-position="${option.position}"
                    ${isSelected ? 'checked' : ''}
                  >
                  <label
                    for="custom-${product.id}-${option.position}-${index}"
                    class="custom-color-swatch${isSelected ? ' selected' : ''}"
                    data-color-token="${this.escapeHtml(info.token)}"
                    data-color-name="${this.escapeHtml(info.label)}"
                    style="background-color: ${info.hex};"
                    title="${this.escapeHtml(info.label)}"
                  >
                    <span class="visually-hidden">${this.escapeHtml(info.label)}</span>
                  </label>
                `;
              }).join('')}
            </div>
          </div>
        `;
      }

      return `
        <div class="custom-field">
          <label for="custom-option-${product.id}-${option.position}">${option.name}</label>
          <select
            id="custom-option-${product.id}-${option.position}"
            name="options[${option.name}]"
            class="custom-option-select"
            data-option-position="${option.position}"
          >
            ${option.values.map(value => `
              <option value="${value}" ${value === selectedValue ? 'selected' : ''}>${value}</option>
            `).join('')}
          </select>
        </div>
      `;
    }).join('');
  }

  buildMetafieldColors(product) {
    const slotSource = product.metafields && product.metafields.colorSlots;
    const colorSource = product.metafields && product.metafields.colors;
    const slots = Number(slotSource) || 0;
    const colors = Array.isArray(colorSource) ? colorSource : [];

    if (!colors.length) {
      return '';
    }

    const limitTemplate = this.strings.colorLimitNote || 'Select up to {{count}} colors';
    const statusTemplate = this.strings.colorSelectedStatus || 'Selected {{selected}} of {{max}} colors';
    const unlimitedText = this.strings.colorLimitUnlimited || 'Select as many colors as you like';
    const countTemplateOther = this.strings.colorSelectedCountOther || 'Selected {{count}} colors';
    const limitText = slots === 0 ? unlimitedText : this.interpolateTemplate(limitTemplate, { count: slots });
    const statusText = slots === 0 ? this.interpolateTemplate(countTemplateOther, { count: 0 }) : this.interpolateTemplate(statusTemplate, { selected: 0, max: slots });

    const swatches = colors.map((color, index) => {
      const info = this.normalizeColorToken(color);
      return `
        <input
          type="checkbox"
          id="custom-${product.id}-color-${index}"
          name="custom-color-choice"
          value="${this.escapeHtml(info.label)}"
          class="visually-hidden custom-color-choice"
          data-color-name="${this.escapeHtml(info.label)}"
          data-color-token="${this.escapeHtml(info.token)}"
        >
        <label
          for="custom-${product.id}-color-${index}"
          class="custom-color-swatch"
          data-color-token="${this.escapeHtml(info.token)}"
          data-color-name="${this.escapeHtml(info.label)}"
          style="background-color: ${info.hex};"
          title="${this.escapeHtml(info.label)}"
        >
          <span class="visually-hidden">${this.escapeHtml(info.label)}</span>
        </label>
      `;
    }).join('');

    return `
      <div class="custom-field custom-color-field" data-color-field>
        <label class="custom-color-field__heading">${this.strings.chooseColors || 'Choose your colors'}</label>
        <p class="custom-color-field__helper" data-role="color-helper">${limitText}</p>
        <div class="custom-color-grid custom-color-swatches" data-color-grid data-max="${slots}">
          ${swatches}
        </div>
        <p class="custom-color-status" data-role="color-status" aria-live="polite">${statusText}</p>
        <input type="hidden" name="properties[Selected Colors]" class="custom-selected-colors" value="">
      </div>
    `;
  }

  buildMaterialsHTML(materials) {
    if (!materials.length) {
      return '';
    }

    return `
      <div class="custom-field">
        <label>${this.strings.materials || 'Materials (+€5 each)'}</label>
        <div class="custom-materials">
          ${materials.map((material, index) => `
            <label style="display: flex; align-items: center; margin-bottom: 0.5rem;">
              <input
                type="checkbox"
                name="properties[Material ${index + 1}]"
                value="${material}"
                class="custom-material-checkbox"
                data-price="500"
              >
              <span>${material} (+€5)</span>
            </label>
          `).join('')}
        </div>
      </div>
    `;
  }

  buildCustomizationNoteHTML(product) {
    // Only show for breakup bag products
    if (product.handle !== 'break-up-bag-mini-copy' && product.handle !== 'break-up-bag-regular-copy') {
      return '';
    }

    const label = this.strings.customizationNote || 'Customization Note';
    const placeholder = this.strings.customizationNotePlaceholder || 'Add any special requests or customization details here...';

    return `
      <div class="custom-field">
        <label for="custom-note-${product.id}">${label}</label>
        <textarea
          id="custom-note-${product.id}"
          name="properties[Customization Note]"
          rows="3"
          placeholder="${this.escapeHtml(placeholder)}"
          style="width: 100%; padding: 0.75rem; border: 1px solid rgba(var(--color-foreground), 0.2); border-radius: 0; font-size: 1rem; font-family: inherit; resize: vertical;"
        ></textarea>
      </div>
    `;
  }

  buildPresetsHTML(presets, product, rules) {
    if (!presets.length) {
      return '';
    }

    const options = presets.map((preset, index) => {
      let dataAttrs = '';
      if (rules && Array.isArray(rules.presets)) {
        const match = rules.presets.find(entry => entry.name === preset);
        if (match) {
          dataAttrs = ` data-big="${match.big || 0}" data-small="${match.small || 0}"`;
        }
      }

      return `<option value="${preset}"${index === 0 ? '' : ''}${dataAttrs}>${preset}</option>`;
    }).join('');

    return `
      <div class="custom-field">
        <label for="custom-preset-${product.id}">Choose Bead Preset</label>
        <select id="custom-preset-${product.id}" name="properties[Preset]" class="custom-preset-select">
          <option value="">Select a preset...</option>
          ${options}
        </select>
      </div>
    `;
  }

  buildBeadGalleryHTML(beadTypes, beadImages, beadSizes) {
    const groups = this.getBeadItemsBySize(beadTypes, beadImages, beadSizes);
    if (!groups.big && !groups.small) {
      return '';
    }

    const label = this.strings.availableCharms || 'Available charms';
    const bigLabel = this.strings.bigCharms || 'Big charms';
    const smallLabel = this.strings.smallCharms || 'Small charms';
    const dragHint = this.strings.dragHere || 'Drag charms here';
    const bigZoneLabel = this.strings.bigCharmsDropzone || `${bigLabel} dropzone`;
    const smallZoneLabel = this.strings.smallCharmsDropzone || `${smallLabel} dropzone`;
    const bigPriceNote = this.strings.bigCharmPriceNote || '+€5 for every big charm you add';
    const smallPriceNote = this.strings.smallCharmPriceNote || '+€3 for every small charm you add';

    const renderGroup = ({ size, title, zoneLabel, items, priceNote }) => `
      <div class="custom-bead-column custom-bead-column--${size}" data-size="${size}">
        <div class="custom-dropzone" data-size="${size}" tabindex="0" aria-label="${zoneLabel}">
          <div class="custom-dropzone__header">
            <span class="custom-dropzone__title">${title}</span>
            <span class="custom-dropzone__counter"><span data-role="dropzone-count">0</span>/<span data-role="dropzone-limit">0</span></span>
          </div>
          <div class="custom-dropzone__body" data-role="dropzone-list"></div>
          <p class="custom-dropzone__hint">${dragHint}</p>
          <p class="custom-dropzone__pricing">${priceNote}</p>
        </div>
        <div class="custom-bead-gallery" data-role="bead-gallery" data-size="${size}" aria-label="${title}">
          ${items || ''}
        </div>
      </div>
    `;

    return `
      <div class="custom-field custom-bead-selector" data-bead-selector="true">
        <label class="custom-bead-selector__label">${label}</label>
        <div class="custom-bead-layout">
          ${renderGroup({ size: 'big', title: bigLabel, zoneLabel: bigZoneLabel, items: groups.big, priceNote: bigPriceNote })}
          ${renderGroup({ size: 'small', title: smallLabel, zoneLabel: smallZoneLabel, items: groups.small, priceNote: smallPriceNote })}
        </div>
        <div class="custom-bead-summary" data-role="bead-summary"></div>
        <p class="custom-bead-status" data-role="bead-status" aria-live="polite"></p>
        <input type="hidden" name="properties[Selected Charms]" class="custom-selected-charms" value="">
      </div>
    `;
  }

  injectBeadGallery(product) {
    if (!product || !this.content) {
      return;
    }

    const beadTypes = Array.isArray(product.metafields && product.metafields.beadTypes) ? product.metafields.beadTypes : [];
    const beadImages = Array.isArray(product.metafields && product.metafields.beadImages) ? product.metafields.beadImages : [];
    const beadSizes = Array.isArray(product.metafields && product.metafields.beadSizes) ? product.metafields.beadSizes : [];
    const beadInventory = product.metafields && product.metafields.beadInventory ? product.metafields.beadInventory : null;
    this.currentBeadTypes = Array.isArray(beadTypes) ? beadTypes.slice() : [];
    this.currentBeadImages = Array.isArray(beadImages) ? beadImages.slice() : [];
    this.currentBeadSizes = Array.isArray(beadSizes) ? beadSizes.slice() : [];
    this.currentBeadInventory = this.parseBeadInventory(beadInventory);

    let selector = this.content.querySelector('[data-bead-selector]');
    if (!selector) {
      const placeholder = this.content.querySelector('.custom-bead-gallery');
      if (placeholder) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = this.buildBeadGalleryHTML(beadTypes, beadImages, beadSizes);
        const field = wrapper.firstElementChild;
        placeholder.replaceWith(field);
        selector = field;
      }
    }

    if (!selector) {
      return;
    }

    const galleries = selector.querySelectorAll('[data-role="bead-gallery"]');
    if (galleries.length) {
      const groups = this.getBeadItemsBySize(beadTypes, beadImages, beadSizes);
      galleries.forEach(gallery => {
        const size = gallery.dataset.size === 'small' ? 'small' : 'big';
        gallery.innerHTML = groups[size];
      });
    }
  }

  getBeadItemsBySize(beadTypes, beadImages, beadSizes) {
    if (!Array.isArray(beadTypes) || !beadTypes.length) {
      return { big: '', small: '' };
    }

    const groups = { big: [], small: [] };

    beadTypes.forEach((name, index) => {
      const sizeValue = Array.isArray(beadSizes) && beadSizes[index] ? String(beadSizes[index]).toLowerCase() : 'big';
      const size = sizeValue === 'small' ? 'small' : 'big';
      const image = Array.isArray(beadImages) ? beadImages[index] : null;
      groups[size].push(this.renderBeadCardHTML(name, image, size));
    });

    return {
      big: groups.big.join(''),
      small: groups.small.join('')
    };
  }

  renderBeadCardHTML(name, image, size) {
    const safeName = this.escapeHtml(name);
    const hasImage = Boolean(image);
    const inStock = this.isBeadInStock(name);
    const inventory = this.getBeadInventory(name);
    const outOfStockClass = inStock ? '' : ' is-out-of-stock';
    const draggable = inStock ? 'true' : 'false';
    const inventoryAttr = inventory !== null ? ` data-inventory="${inventory}"` : '';

    const imageMarkup = hasImage
      ? `<div class="custom-bead-card__image"><img src="${image}" alt="${safeName}" loading="lazy"></div>`
      : `<div class="custom-bead-card__image custom-bead-card__image--placeholder">${safeName.charAt(0)}</div>`;

    const outOfStockBadge = inStock ? '' : '<span class="custom-bead-card__out-of-stock-badge">Out of Stock</span>';

    return `
      <figure class="custom-bead-card${outOfStockClass}" data-charm="${safeName}" data-size="${size}" draggable="${draggable}" aria-roledescription="Draggable charm"${inventoryAttr}>
        ${imageMarkup}
        <figcaption>${safeName}</figcaption>
        ${outOfStockBadge}
      </figure>
    `;
  }

  setupCustomizerListeners() {
    if (!this.activeForm || !this.panel) {
      return;
    }

    if (this.currentForm && this.currentForm !== this.activeForm) {
      this.currentForm.removeEventListener('change', this.boundFormChange);
      this.currentForm.removeEventListener('submit', this.boundFormSubmit);
    }

    if (this.currentForm !== this.activeForm) {
      this.currentForm = this.activeForm;
      this.currentForm.addEventListener('change', this.boundFormChange);
      this.currentForm.addEventListener('submit', this.boundFormSubmit);
    }

    this.syncAllColorSwatches();
    this.applySwatchBackgrounds(this.activeForm);
    this.setupMediaThumbnails();
    this.setupColorGrid();

    const presetSelect = this.activeForm ? this.activeForm.querySelector('.custom-preset-select') : null;
    if (presetSelect) {
      presetSelect.addEventListener('change', () => {
        this.handlePresetChange();
      });
    }

    this.setupDragAndDrop();
  }

  setupMediaThumbnails() {
    if (!this.panel) {
      return;
    }

    const mainImage = this.panel.querySelector('.custom-main-image');
    if (!mainImage) {
      return;
    }

    const thumbnails = this.panel.querySelectorAll('.product__media-thumbnail');
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        const image = thumbnail.querySelector('img');
        if (!image) {
          return;
        }
        const full = image.dataset.fullSrc || image.src;
        mainImage.src = full;
      });
    });
  }

  handleFormChange(event) {
    const target = event.target;
    if (!target) {
      return;
    }

    if (target.matches('.custom-option-input')) {
      this.syncColorSwatchGroup(target.name);
      this.handleVariantChange();
      return;
    }

    if (target.matches('select[data-option-position]')) {
      this.handleVariantChange();
      return;
    }

    if (target.matches('.custom-color-choice')) {
      this.handleColorChoiceToggle(target);
      return;
    }

    if (target.matches('.custom-material-checkbox')) {
      this.updatePrice();
      return;
    }

    if (target.closest('.custom-bead-selector')) {
      return;
    }
  }

  syncVariantId() {
    if (!this.activeForm || !this.selectedVariant) {
      return;
    }

    const input = this.activeForm.querySelector('.custom-variant-id');
    if (input) {
      input.value = this.selectedVariant.id;
    }
  }

  syncColorSwatchGroup(name) {
    if (!this.activeForm || !name) {
      return;
    }

    const inputs = this.activeForm.querySelectorAll(`input[name="${name}"]`);
    inputs.forEach(input => {
      const label = this.activeForm.querySelector(`label[for="${input.id}"]`);
      if (label) {
        label.classList.toggle('selected', input.checked);
      }
    });
  }

  syncAllColorSwatches() {
    if (!this.activeForm) {
      return;
    }

    const inputs = this.activeForm.querySelectorAll('.custom-option-input[type="radio"], .custom-color-input, .custom-color-choice');
    const names = new Set();
    inputs.forEach(input => {
      names.add(input.name);
    });
    names.forEach(name => {
      this.syncColorSwatchGroup(name);
    });
  }

  applySwatchBackgrounds(root) {
    const scope = root || this.activeForm || this.content;
    if (!scope) {
      return;
    }

    const swatches = scope.querySelectorAll('.custom-color-swatch');
    swatches.forEach(swatch => {
      const token = swatch.dataset.colorToken || swatch.dataset.colorName || swatch.getAttribute('title') || '';
      if (!token) {
        return;
      }
      const hex = this.getColorHex(token);
      if (hex) {
        swatch.style.setProperty('background-color', hex);
      }
    });
  }

  setupColorGrid() {
    if (!this.activeForm) {
      return;
    }

    const grid = this.activeForm.querySelector('[data-color-grid]');
    if (!grid) {
      return;
    }

    const selectedColorsInput = this.activeForm.querySelector('.custom-selected-colors');
    const presetValue = selectedColorsInput && selectedColorsInput.value
      ? selectedColorsInput.value.split('|').map(item => item.trim()).filter(Boolean)
      : [];

    const inputs = grid.querySelectorAll('.custom-color-choice');
    if (presetValue.length) {
      inputs.forEach(input => {
        const name = input.dataset.colorName || input.value;
        input.checked = presetValue.includes(name);
      });
    }

    this.syncColorChoiceState(grid);
    this.updateSelectedColorsInput();
  }

  handleColorChoiceToggle(input) {
    const grid = input.closest('[data-color-grid]');
    if (!grid) {
      return;
    }

    const max = Number(grid.dataset.max || 0);
    if (input.checked && max > 0) {
      const selected = grid.querySelectorAll('.custom-color-choice:checked');
      if (selected.length > max) {
        input.checked = false;
        this.announceColorLimit(grid, max);
        return;
      }
    }

    this.syncColorChoiceState(grid);
    this.updateSelectedColorsInput();
  }

  syncColorChoiceState(grid) {
    if (!grid) {
      return;
    }

    const inputs = grid.querySelectorAll('.custom-color-choice');
    inputs.forEach(input => {
      const label = grid.querySelector(`label[for="${input.id}"]`);
      if (label) {
        label.classList.toggle('selected', input.checked);
      }
    });

    this.applySwatchBackgrounds(grid);
    this.updateColorStatus(grid);
  }

  updateSelectedColorsInput() {
    if (!this.activeForm) {
      return;
    }

    const grid = this.activeForm.querySelector('[data-color-grid]');
    const hidden = this.activeForm.querySelector('.custom-selected-colors');
    if (!grid || !hidden) {
      return;
    }

    const names = Array.from(grid.querySelectorAll('.custom-color-choice:checked')).map(input => input.dataset.colorName || input.value);
    hidden.value = names.join(' | ');
  }

  updateColorStatus(grid) {
    if (!this.activeForm) {
      return;
    }

    const status = this.activeForm.querySelector('[data-role="color-status"]');
    if (!status) {
      return;
    }

    clearTimeout(this.colorStatusTimer);

    const max = Number(grid && grid.dataset.max ? grid.dataset.max : 0);
    const selected = grid ? grid.querySelectorAll('.custom-color-choice:checked').length : 0;
    const template = this.strings.colorSelectedStatus || 'Selected {{selected}} of {{max}} colors';

    if (max === 0) {
      const countTemplateOne = this.strings.colorSelectedCountOne || 'Selected {{count}} color';
      const countTemplateOther = this.strings.colorSelectedCountOther || 'Selected {{count}} colors';
      const countTemplate = selected === 1 ? countTemplateOne : countTemplateOther;
      status.textContent = this.interpolateTemplate(countTemplate, { count: selected });
    } else {
      status.textContent = this.interpolateTemplate(template, { selected, max });
    }
  }

  announceColorLimit(grid, max) {
    if (!this.activeForm) {
      return;
    }

    const status = this.activeForm.querySelector('[data-role="color-status"]');
    if (!status) {
      return;
    }

    const template = this.strings.colorLimitReached || 'You can select up to {{max}} colors.';
    status.textContent = this.interpolateTemplate(template, { max });

    clearTimeout(this.colorStatusTimer);
    this.colorStatusTimer = setTimeout(() => {
      this.updateColorStatus(grid);
    }, 1600);
  }

  handleVariantChange() {
    if (!this.activeForm || !this.selectedProduct || !Array.isArray(this.selectedProduct.variants)) {
      return;
    }

    const selectedOptions = this.getSelectedOptions();
    if (!selectedOptions.length) {
      return;
    }

    const variant = this.selectedProduct.variants.find(candidate => {
      return candidate.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) {
      return;
    }

    this.selectedVariant = variant;
    this.syncVariantId();

    if (variant.featuredImage) {
      const mainImage = this.panel ? this.panel.querySelector('.custom-main-image') : null;
      if (mainImage) {
        mainImage.src = variant.featuredImage;
      }
    }

    const submitButton = this.activeForm.querySelector('.button[type="submit"]');
    if (submitButton) {
      if (variant.available === false) {
        submitButton.setAttribute('disabled', 'disabled');
        submitButton.textContent = this.strings.soldOut || 'Sold out';
      } else {
        submitButton.textContent = this.strings.addToCart || 'Add to cart';
        if (!this.requiresBeadSelection) {
          submitButton.removeAttribute('disabled');
        }
      }
    }

    this.updateCharmStatus();
    this.updatePrice();
  }

  getSelectedOptions() {
    if (!this.activeForm) {
      return [];
    }

    const optionNodes = Array.from(this.activeForm.querySelectorAll('[data-option-position]'));
    if (!optionNodes.length) {
      return [];
    }

    const positions = Array.from(new Set(optionNodes.map(node => Number(node.dataset.optionPosition)))).sort((a, b) => a - b);
    const selections = [];

    positions.forEach(position => {
      const group = optionNodes.filter(node => Number(node.dataset.optionPosition) === position);
      if (!group.length) {
        return;
      }
      const first = group[0];
      if (first.tagName === 'SELECT') {
        selections[position - 1] = first.value;
      } else {
        const checked = group.find(node => node.checked);
        if (checked) {
          selections[position - 1] = checked.value;
        }
      }
    });

    return selections;
  }

  updatePrice() {
    if (!this.activeForm) {
      return;
    }

    const base = this.selectedVariant ? this.selectedVariant.price : this.selectedProduct ? this.selectedProduct.price : 0;
    let total = base;

    const materialCheckboxes = this.activeForm.querySelectorAll('.custom-material-checkbox:checked');
    materialCheckboxes.forEach(checkbox => {
      const price = parseInt(checkbox.dataset.price || '0', 10);
      if (!Number.isNaN(price)) {
        total += price;
      }
    });

    total += this.calculateCharmSurcharge();

    const priceElement = this.panel ? this.panel.querySelector(`#custom-price-${this.selectedProduct.id}`) : null;
    if (priceElement) {
      priceElement.textContent = this.formatMoney(total);
    }
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.activeForm || !this.selectedVariant) {
      return;
    }

    const formData = new FormData(this.activeForm);
    const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;
    const properties = {};

    if (this.requiresBeadSelection) {
      if (!this.charmPresetLimits) {
        this.updateCharmStatus('selectPreset');
        return;
      }

      if (!this.isCharmSelectionComplete()) {
        this.updateCharmStatus();
        return;
      }
    }

    formData.forEach((value, key) => {
      if (!value) {
        return;
      }
      if (key.startsWith('properties[') && key.endsWith(']')) {
        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
      }
    });

    const payload = {
      items: [
        {
          id: this.selectedVariant.id,
          quantity,
          properties
        }
      ]
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        window.location.href = '/cart';
      } else {
        console.error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  scrollToCustomizer() {
    if (!this.panel) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const focusable = this.panel.querySelector('input:not([type="hidden"]), select, button');
      if (focusable) {
        focusable.focus();
      }
    });
  }

  toggleProductGrid(show) {
    if (!this.grid) {
      return;
    }

    this.grid.classList.toggle('is-hidden', !show);
  }

  handleBackClick() {
    this.clearSelectionState();
    this.updateUrl(null);
    if (this.grid) {
      this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  clearSelectionState() {
    this.selectedProduct = null;
    this.selectedVariant = null;
    if (this.content) {
      this.content.innerHTML = '';
    }
    if (this.panel) {
      this.panel.classList.remove('active');
    }
    this.currentBeadTypes = [];
    this.currentBeadImages = [];
    this.currentBeadInventory = {};
    this.updateCardSelection('');
    this.toggleProductGrid(true);
  }

  applyFallback(product) {
    if (!product) {
      return;
    }

    const fallback = this.fallback[product.handle];
    if (!fallback) {
      return;
    }

    if (!product.metafields) {
      product.metafields = {};
    }

    const meta = product.metafields;

    if (fallback.colorSlots !== undefined && fallback.colorSlots !== null) {
      // Always use fallback colorSlots to allow overriding metafields
      meta.colorSlots = fallback.colorSlots;
    }

    if ((!Array.isArray(meta.colors) || !meta.colors.length) && Array.isArray(fallback.colors)) {
      meta.colors = fallback.colors;
    }

    if ((!Array.isArray(meta.materials) || !meta.materials.length) && Array.isArray(fallback.materials)) {
      meta.materials = fallback.materials;
    }

    if ((!Array.isArray(meta.presets) || !meta.presets.length) && Array.isArray(fallback.presets)) {
      meta.presets = fallback.presets;
    }

    if ((!Array.isArray(meta.beadTypes) || !meta.beadTypes.length) && Array.isArray(fallback.beadTypes)) {
      meta.beadTypes = fallback.beadTypes;
    }

    if ((!Array.isArray(meta.beadImages) || !meta.beadImages.length) && Array.isArray(fallback.beadImages)) {
      meta.beadImages = fallback.beadImages;
    }

    if (Array.isArray(meta.beadTypes)) {
      if (!Array.isArray(meta.beadImages)) {
        meta.beadImages = [];
      }

      const fallbackImageMap = fallback.beadImageMap || null;

      if (fallbackImageMap) {
        meta.beadTypes.forEach((beadName, index) => {
          if (!meta.beadImages[index] && Object.prototype.hasOwnProperty.call(fallbackImageMap, beadName)) {
            meta.beadImages[index] = fallbackImageMap[beadName];
          }
        });
      }
    }

    if ((!Array.isArray(meta.beadSizes) || !meta.beadSizes.length) && Array.isArray(fallback.beadSizes)) {
      meta.beadSizes = fallback.beadSizes;
    }

    if (!meta.rulesJson && fallback.rulesJson) {
      meta.rulesJson = fallback.rulesJson;
    }

    if (!meta.subtitle && fallback.subtitle) {
      meta.subtitle = fallback.subtitle;
    }
  }

  showLoadingState() {
    if (!this.panel || !this.content) {
      return;
    }

    this.panel.classList.add('active');
    this.content.innerHTML = '<div class="custom-loading">Loading customization...</div>';
  }

  initializeCharmState() {
    const gallery = this.activeForm ? this.activeForm.querySelector('[data-role="bead-gallery"]') : null;
    if (!gallery || !this.selectedProduct) {
      this.requiresBeadSelection = false;
      this.charmSelection = null;
      this.currentRules = null;
      this.charmPresetLimits = null;
      this.updateCharmStatus();
      this.updateSelectedCharmsInput();
      this.updateAddToCartState(true);
      this.renderDropzoneItems([]);
      this.updateDropzoneCounts();
      this.updateDropzoneLimits();
      this.updateCharmCardSelectionStates();
      return;
    }

    this.requiresBeadSelection = true;
    this.currentRules = this.parseRules(this.selectedProduct.metafields ? this.selectedProduct.metafields.rulesJson : null);
    this.charmSelection = {
      totals: { big: 0, small: 0 },
      byCharm: {}
    };
    this.setPresetLimits(this.getPresetSelectValue());
    this.updateCharmSummary();
    this.updateSelectedCharmsInput();
  }

  handlePresetChange() {
    if (!this.requiresBeadSelection) {
      return;
    }

    this.charmSelection = {
      totals: { big: 0, small: 0 },
      byCharm: {}
    };
    this.setPresetLimits(this.getPresetSelectValue());
    this.updateCharmSummary();
    this.updateSelectedCharmsInput();
    this.updatePrice();

    // Scroll to dropzones after preset selection
    if (this.activeForm) {
      const firstDropzone = this.activeForm.querySelector('.custom-dropzone');
      if (firstDropzone) {
        setTimeout(() => {
          firstDropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }

  setupDragAndDrop() {
    if (!this.activeForm) {
      return;
    }

    const cards = this.activeForm.querySelectorAll('.custom-bead-card');
    cards.forEach(card => {
      card.addEventListener('dragstart', event => {
        this.handleBeadDragStart(event, card);
      });
      card.addEventListener('dragend', () => {
        this.handleBeadDragEnd(card);
      });
    });

    const dropzones = this.activeForm.querySelectorAll('.custom-dropzone');
    dropzones.forEach(zone => {
      zone.addEventListener('dragenter', event => {
        this.handleDropzoneDragEnter(event, zone);
      });
      zone.addEventListener('dragover', event => {
        this.handleDropzoneDragOver(event, zone);
      });
      zone.addEventListener('dragleave', event => {
        this.handleDropzoneDragLeave(event, zone);
      });
      zone.addEventListener('drop', event => {
        this.handleDropzoneDrop(event, zone);
      });
      zone.addEventListener('click', event => {
        this.handleDropzoneClick(event);
      });
    });
  }

  handleBeadDragStart(event, card) {
    if (!card) {
      return;
    }
    const name = String(card.dataset.charm || '').trim();
    if (!name) {
      return;
    }

    if (!this.isBeadInStock(name)) {
      event.preventDefault();
      this.updateCharmStatus('This bead is out of stock and cannot be selected');
      return;
    }

    const size = String(card.dataset.size || 'big').trim();
    this.draggedCharm = name;
    this.draggedCharmSize = size;
    card.classList.add('is-dragging');
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', name);
      event.dataTransfer.setData('application/bead-size', size);
      event.dataTransfer.effectAllowed = 'copy';
    }
  }

  handleBeadDragEnd(card) {
    if (card) {
      card.classList.remove('is-dragging');
    }
    this.draggedCharm = null;
    this.draggedCharmSize = null;
    this.clearDropzoneStates();
  }

  handleDropzoneDragEnter(event, zone) {
    event.preventDefault();
    if (!zone) {
      return;
    }
    if (!this.charmPresetLimits) {
      zone.classList.add('is-disabled');
      zone.classList.remove('is-active-drop');
      zone.classList.remove('is-invalid-drop');
      return;
    }
    zone.classList.add('is-active-drop');
  }

  handleDropzoneDragOver(event, zone) {
    if (!zone) {
      return;
    }
    if (!this.charmPresetLimits) {
      zone.classList.add('is-disabled');
      zone.classList.remove('is-active-drop');
      zone.classList.remove('is-invalid-drop');
      return;
    }
    const dropzoneSize = zone.dataset.size;

    // Check if dragged bead size matches dropzone size
    const beadSize = (event.dataTransfer && event.dataTransfer.types.includes('application/bead-size'))
      ? event.dataTransfer.getData('application/bead-size') || this.draggedCharmSize
      : this.draggedCharmSize || 'big';

    if (beadSize && beadSize !== dropzoneSize) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      zone.classList.add('is-invalid-drop');
      zone.classList.remove('is-active-drop');
      event.preventDefault();
      return;
    }

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    zone.classList.add('is-active-drop');
    zone.classList.remove('is-invalid-drop');
    event.preventDefault();
  }

  handleDropzoneDragLeave(event, zone) {
    if (!zone || !zone.contains(event.relatedTarget)) {
      if (zone) {
        zone.classList.remove('is-active-drop');
        zone.classList.remove('is-invalid-drop');
      }
    }
  }

  handleDropzoneDrop(event, zone) {
    event.preventDefault();
    const name = (event.dataTransfer && event.dataTransfer.getData('text/plain')) || this.draggedCharm;
    const beadSize = (event.dataTransfer && event.dataTransfer.getData('application/bead-size')) || this.draggedCharmSize;
    if (!zone || !name) {
      this.clearDropzoneStates();
      return;
    }
    this.clearDropzoneStates();
    this.draggedCharm = null;
    this.draggedCharmSize = null;
    this.attemptDrop(zone, name, beadSize);
  }

  attemptDrop(zone, charmName, beadSize) {
    if (!zone || !charmName) {
      return;
    }
    const dropzoneSize = zone.dataset.size;
    if (!dropzoneSize) {
      return;
    }
    const name = String(charmName).trim();
    if (!name) {
      return;
    }

    if (!this.isBeadInStock(name)) {
      this.updateCharmStatus('This bead is out of stock and cannot be selected');
      return;
    }

    // Check if bead size matches dropzone size
    const actualBeadSize = beadSize || 'big';
    if (actualBeadSize !== dropzoneSize) {
      this.flashDropzoneInvalidSize(zone, dropzoneSize, actualBeadSize);
      return;
    }

    if (!this.charmPresetLimits) {
      this.flashDropzonePreset(dropzoneSize);
      this.updateCharmStatus('selectPreset');
      return;
    }
    this.addCharmSelection(name, dropzoneSize);
    this.signalDropSuccess(dropzoneSize);
  }

  handleDropzoneClick(event) {
    const button = event.target.closest('[data-action="remove-charm"]');
    if (!button) {
      return;
    }
    event.preventDefault();
    const size = button.dataset.size;
    const name = String(button.dataset.charm || '').trim();
    if (!size || !name) {
      return;
    }
    this.removeCharmSelection(name, size);
  }

  clearDropzoneStates() {
    if (!this.activeForm) {
      return;
    }
    const zones = this.activeForm.querySelectorAll('.custom-dropzone');
    zones.forEach(zone => {
      zone.classList.remove('is-active-drop');
      zone.classList.remove('is-invalid-drop');
    });
  }

  flashDropzonePreset(size) {
    const zone = this.getDropzone(size);
    if (!zone) {
      return;
    }
    zone.classList.add('is-disabled');
    const key = `preset-${size}`;
    clearTimeout(this.dropzoneTimers[key]);
    this.dropzoneTimers[key] = setTimeout(() => {
      zone.classList.remove('is-disabled');
    }, 800);
  }

  flashDropzoneInvalidSize(zone, dropzoneSize, beadSize) {
    if (!zone) {
      return;
    }
    zone.classList.add('is-invalid-drop');
    const key = `invalid-size-${dropzoneSize}`;
    clearTimeout(this.dropzoneTimers[key]);
    this.dropzoneTimers[key] = setTimeout(() => {
      zone.classList.remove('is-invalid-drop');
    }, 600);

    // Update status message to inform user
    const sizeLabel = beadSize === 'big' ? (this.strings.bigCharms || 'Big charms') : (this.strings.smallCharms || 'Small charms');
    const dropzoneLabel = dropzoneSize === 'big' ? (this.strings.bigCharms || 'Big charms') : (this.strings.smallCharms || 'Small charms');
    const message = `${sizeLabel} can only be added to the ${dropzoneLabel.toLowerCase()} dropzone`;
    this.updateCharmStatus(message);
  }

  getDropzone(size) {
    if (!this.activeForm) {
      return null;
    }
    return this.activeForm.querySelector(`.custom-dropzone[data-size="${size}"]`);
  }

  signalDropSuccess(size) {
    const zone = this.getDropzone(size);
    if (!zone) {
      return;
    }
    zone.classList.remove('is-invalid-drop');
    zone.classList.remove('is-disabled');
    zone.classList.add('is-success-drop');
    const key = `success-${size}`;
    clearTimeout(this.dropzoneTimers[key]);
    this.dropzoneTimers[key] = setTimeout(() => {
      zone.classList.remove('is-success-drop');
    }, 500);
  }

  getPresetLimit(size) {
    if (!this.charmPresetLimits) {
      return 0;
    }
    return Number(this.charmPresetLimits[size] || 0);
  }

  getSelectedCount(size) {
    if (!this.charmSelection || !this.charmSelection.totals) {
      return 0;
    }
    return Number(this.charmSelection.totals[size] || 0);
  }

  updateDropzoneLimits() {
    if (!this.activeForm) {
      return;
    }
    const dropzones = this.activeForm.querySelectorAll('.custom-dropzone');
    dropzones.forEach(zone => {
      const size = zone.dataset.size;
      const limit = this.getPresetLimit(size);
      const limitNode = zone.querySelector('[data-role="dropzone-limit"]');
      if (limitNode) {
        if (this.charmPresetLimits) {
          limitNode.textContent = limit > 0 ? `${limit}+` : '0+';
        } else {
          limitNode.textContent = '0';
        }
      }
      const presetActive = Boolean(this.charmPresetLimits);
      const shouldDisable = !presetActive;
      zone.classList.toggle('is-disabled', shouldDisable);
      zone.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      if (shouldDisable) {
        zone.classList.remove('is-active-drop');
      }
    });
  }

  updateDropzoneCounts() {
    if (!this.activeForm) {
      return;
    }
    const dropzones = this.activeForm.querySelectorAll('.custom-dropzone');
    dropzones.forEach(zone => {
      const size = zone.dataset.size;
      const countNode = zone.querySelector('[data-role="dropzone-count"]');
      if (countNode) {
        countNode.textContent = this.getSelectedCount(size);
      }
      const limit = this.getPresetLimit(size);
      const current = this.getSelectedCount(size);
      zone.classList.toggle('is-full', this.charmPresetLimits && limit > 0 && current >= limit);
    });
  }

  renderDropzoneItems(entries) {
    if (!this.activeForm) {
      return;
    }

    const dropzones = this.activeForm.querySelectorAll('.custom-dropzone');
    dropzones.forEach(zone => {
      const size = zone.dataset.size;
      const list = zone.querySelector('[data-role="dropzone-list"]');
      if (!list) {
        return;
      }
      const items = (entries || this.getCharmEntries())
        .map(([name, counts]) => {
          if (!counts[size]) {
            return '';
          }
          return this.buildDropzoneItemHTML(name, counts[size], size);
        })
        .filter(Boolean)
        .join('');

      // Check current state BEFORE updating classes
      const wasEmpty = zone.classList.contains('is-empty');
      const willBeEmpty = !items;
      
      if (items) {
        list.innerHTML = items;
        list.classList.remove('is-empty');
        zone.classList.remove('is-empty');
      } else {
        list.innerHTML = '';
        list.classList.add('is-empty');
        zone.classList.add('is-empty');
      }
      
      // Update hint text with transition states
      this.updateDropzoneHintText(zone, size, willBeEmpty, wasEmpty);
    });
  }

  updateDropzoneHintText(zone, size, willBeEmpty, wasEmpty) {
    const hintElement = zone.querySelector('.custom-dropzone__hint');
    const headerElement = zone.querySelector('.custom-dropzone__header');
    if (!hintElement) {
      return;
    }
    
    // Calculate transition states
    const isBecomingFilled = wasEmpty && !willBeEmpty;
    const isBecomingEmpty = !wasEmpty && willBeEmpty;
    
    if (willBeEmpty) {
      // Set size-specific hint text for empty dropzones
      const bigHint = this.strings.dragBigCharms || 'Drag big charms here';
      const smallHint = this.strings.dragSmallCharms || 'Drag small charms here';
      const newText = size === 'big' ? bigHint : smallHint;
      
      if (isBecomingEmpty) {
        // Animate from filled back to empty state
        this.animateTextToEmpty(zone, hintElement, newText);
      } else {
        // Direct set for initial state
        hintElement.textContent = newText;
      }
    } else {
      // Dropzone is getting filled
      if (isBecomingFilled) {
        console.log('Triggering fill animation for zone:', size, 'wasEmpty:', wasEmpty, 'willBeEmpty:', willBeEmpty);
        // Animate the transition from empty to filled
        this.animateTextToFilled(zone, hintElement, headerElement, size);
      } else {
        // Already filled, just set the generic text
        const dragHint = this.strings.dragHere || 'Drag charms here';
        hintElement.textContent = dragHint;
      }
    }
  }
  
  animateTextToFilled(zone, hintElement, headerElement, size) {
    console.log('Starting animation to filled state for size:', size);
    
    // Step 1: Start the move-up animation
    zone.classList.add('is-transitioning', 'move-up');
    
    // Step 2: After partial animation, start revealing header
    setTimeout(() => {
      const titleElement = headerElement ? headerElement.querySelector('.custom-dropzone__title') : null;
      if (titleElement) {
        const bigLabel = this.strings.bigCharms || 'Big charms';
        const smallLabel = this.strings.smallCharms || 'Small charms';
        titleElement.textContent = size === 'big' ? bigLabel : smallLabel;
      }
    }, 400);
    
    // Step 3: Complete the animation and set up final state
    setTimeout(() => {
      console.log('Animation step 3: completing transition');
      
      // Remove transition classes
      zone.classList.remove('is-transitioning', 'move-up');
      
      // Hide the old hint temporarily
      hintElement.style.opacity = '0';
      
      // Set new small hint text
      const dragHint = this.strings.dragHere || 'Drag charms here';
      hintElement.textContent = dragHint;
      
      // Add fade-in animation class
      hintElement.classList.add('fade-in-hint');
      
      // Clean up animation class after it completes
      setTimeout(() => {
        hintElement.classList.remove('fade-in-hint');
        hintElement.style.opacity = '';
      }, 500);
      
    }, 800); // Match CSS transition duration
  }
  
  animateTextToEmpty(zone, hintElement, newText) {
    // Fade out current content first
    zone.style.transition = 'opacity 0.3s ease-out';
    zone.style.opacity = '0.5';
    
    setTimeout(() => {
      hintElement.textContent = newText;
      zone.style.opacity = '';
      zone.style.transition = '';
    }, 300);
  }

  buildDropzoneItemHTML(name, count, size) {
    const displayName = this.escapeHtml(name);
    const removeLabel = this.strings.removeCharm || 'Remove charm';
    const sizeLabel = size === 'big' ? (this.strings.bigCharms || 'Big charms') : (this.strings.smallCharms || 'Small charms');
    const ariaLabel = `${removeLabel} ${displayName} (${sizeLabel})`;
    const badge = count > 1 ? `<span class="custom-dropzone-item__count">×${count}</span>` : '';
    const thumbnail = this.getCharmImageMarkup(name);

    return `
      <div class="custom-dropzone-item" data-charm="${displayName}" data-size="${size}">
        ${thumbnail}
        <span class="custom-dropzone-item__label">${displayName}</span>
        ${badge}
        <button type="button" class="custom-dropzone-item__remove" data-action="remove-charm" data-size="${size}" data-charm="${displayName}" aria-label="${this.escapeHtml(ariaLabel)}">×</button>
      </div>
    `;
  }

  getCharmEntries() {
    if (!this.charmSelection || !this.charmSelection.byCharm) {
      return [];
    }
    return Object.entries(this.charmSelection.byCharm).filter(([, counts]) => counts.big || counts.small);
  }

  getCharmImageMarkup(name) {
    const index = Array.isArray(this.currentBeadTypes) ? this.currentBeadTypes.indexOf(name) : -1;
    if (index > -1) {
      const image = Array.isArray(this.currentBeadImages) ? this.currentBeadImages[index] : null;
      if (image) {
        return `<span class="custom-dropzone-item__thumb"><img src="${image}" alt="${this.escapeHtml(name)}" loading="lazy"></span>`;
      }
    }
    const initial = this.escapeHtml(name.charAt(0) || '?');
    return `<span class="custom-dropzone-item__thumb custom-dropzone-item__thumb--placeholder">${initial}</span>`;
  }

  updateCharmCardSelectionStates() {
    if (!this.activeForm) {
      return;
    }
    const cards = this.activeForm.querySelectorAll('.custom-bead-card');
    cards.forEach((card, index) => {
      const name = card.dataset.charm;
      if (!name) {
        return;
      }
      const record = this.charmSelection && this.charmSelection.byCharm ? this.charmSelection.byCharm[name] : null;
      const isSelected = record && (record.big || record.small);
      card.classList.toggle('selected', Boolean(isSelected));
      
      // Add staggered dance delay for selected cards
      if (isSelected) {
        card.style.setProperty('--dance-delay', (index % 5) * 0.2);
      } else {
        card.style.removeProperty('--dance-delay');
      }
    });
  }

  addCharmSelection(name, size) {
    if (!this.charmPresetLimits) {
      return;
    }

    if (!this.charmSelection.byCharm[name]) {
      this.charmSelection.byCharm[name] = { big: 0, small: 0 };
    }

    this.charmSelection.byCharm[name][size] += 1;
    this.charmSelection.totals[size] = (this.charmSelection.totals[size] || 0) + 1;
    this.updateCharmSummary();
    this.updateSelectedCharmsInput();
    this.updatePrice();
  }

  removeCharmSelection(name, size) {
    const record = this.charmSelection.byCharm[name];
    if (!record || !record[size]) {
      return;
    }

    record[size] -= 1;
    if (record[size] < 0) {
      record[size] = 0;
    }

    this.charmSelection.totals[size] = Math.max((this.charmSelection.totals[size] || 0) - 1, 0);

    if (!record.big && !record.small) {
      delete this.charmSelection.byCharm[name];
    }

    this.updateCharmSummary();
    this.updateSelectedCharmsInput();
    this.updatePrice();
  }

  updateCharmSummary() {
    const summary = this.activeForm ? this.activeForm.querySelector('[data-role="bead-summary"]') : null;
    const entries = this.getCharmEntries();

    // Hide the summary list as it's unnecessary - users can see selections in dropzones
    if (summary) {
      summary.innerHTML = '';
      summary.setAttribute('aria-hidden', 'true');
    }

    this.renderDropzoneItems(entries);
    this.updateDropzoneCounts();
    this.updateCharmCardSelectionStates();
    this.updateCharmStatus();
  }

  updateCharmStatus(mode) {
    const status = this.activeForm ? this.activeForm.querySelector('[data-role="bead-status"]') : null;
    const submitButton = this.activeForm ? this.activeForm.querySelector('.button.button--primary') : null;
    if (!status || !submitButton) {
      return;
    }
    
    // Helper function to set text with animation
    const setStatusText = (text, isCelebration = false) => {
      if (status.textContent !== text && text) {
        status.style.animation = 'none';
        status.offsetHeight; // Trigger reflow
        if (isCelebration) {
          status.style.animation = 'statusCelebration 0.8s ease-out';
        } else {
          status.style.animation = null;
        }
      }
      status.innerHTML = text; // Use innerHTML instead of textContent to support HTML
    };

    const variantAvailable = this.selectedVariant ? this.selectedVariant.available !== false : true;

    if (!this.requiresBeadSelection) {
      setStatusText('');
      if (variantAvailable) {
        submitButton.removeAttribute('disabled');
      }
      return;
    }

    if (!this.charmPresetLimits) {
      const message = this.strings.selectPresetAbove || 'Select a preset above';
      setStatusText(`${message} <span class="bounce-emoji">☝️</span>`);
      submitButton.setAttribute('disabled', 'disabled');
      return;
    }

    if (mode === 'selectPreset') {
      setStatusText('');
      return;
    }

    // Handle custom messages (like invalid size)
    if (typeof mode === 'string' && mode !== 'selectPreset') {
      setStatusText(mode);
      submitButton.setAttribute('disabled', 'disabled');
      return;
    }

    const requiredBig = this.charmPresetLimits.big || 0;
    const requiredSmall = this.charmPresetLimits.small || 0;
    const selectedBig = this.charmSelection && this.charmSelection.totals ? (this.charmSelection.totals.big || 0) : 0;
    const selectedSmall = this.charmSelection && this.charmSelection.totals ? (this.charmSelection.totals.small || 0) : 0;

    const remainingBig = Math.max(requiredBig - selectedBig, 0);
    const remainingSmall = Math.max(requiredSmall - selectedSmall, 0);
    const extraBig = Math.max(selectedBig - requiredBig, 0);
    const extraSmall = Math.max(selectedSmall - requiredSmall, 0);

    if (remainingBig > 0 || remainingSmall > 0) {
      const parts = [];
      if (remainingBig > 0) {
        parts.push(`${remainingBig} big`);
      }
      if (remainingSmall > 0) {
        parts.push(`${remainingSmall} small`);
      }
      const charmText = parts.join(' and ');
      const template = this.strings.dragCharmsInstruction || 'Drag {{charms}} charms into the highlighted boxes above the grid';
      const message = template.replace('{{charms}}', charmText);
      setStatusText(`${message} <span class="bounce-emoji">👇</span>`);
      submitButton.setAttribute('disabled', 'disabled');
      return;
    }

    const extras = [];
    if (extraBig > 0) {
      extras.push(`${extraBig} extra big`);
    }
    if (extraSmall > 0) {
      extras.push(`${extraSmall} extra small`);
    }

    const baseMessage = this.strings.selectionComplete || 'Charm selection complete!';
    if (extras.length) {
      setStatusText(`${baseMessage} (+${extras.join(', ')}) ✅`, true);
    } else {
      setStatusText(`${baseMessage} ✅`, true);
    }

    if (variantAvailable) {
      submitButton.removeAttribute('disabled');
    }
  }

  updateSelectedCharmsInput() {
    const input = this.activeForm ? this.activeForm.querySelector('.custom-selected-charms') : null;
    if (!input) {
      return;
    }

    if (!this.requiresBeadSelection || !this.charmSelection || !this.charmSelection.byCharm) {
      input.value = '';
      return;
    }

    const parts = Object.entries(this.charmSelection.byCharm)
      .filter(([, counts]) => counts.big || counts.small)
      .map(([name, counts]) => {
        const fragments = [];
        if (counts.big) {
          fragments.push(`${counts.big} Big`);
        }
        if (counts.small) {
          fragments.push(`${counts.small} Small`);
        }
        return `${name}: ${fragments.join(', ')}`;
      });

    input.value = parts.join(' | ');
  }

  getPresetSelectValue() {
    const select = this.activeForm ? this.activeForm.querySelector('.custom-preset-select') : null;
    return select ? select.value : '';
  }

  setPresetLimits(name) {
    if (!name) {
      this.charmPresetLimits = null;
      this.updateCharmStatus('selectPreset');
      this.updateDropzoneLimits();
      this.updateDropzoneCounts();
      return;
    }

    if (!this.currentRules || !Array.isArray(this.currentRules.presets)) {
      this.charmPresetLimits = null;
      this.updateCharmStatus();
      this.updateDropzoneLimits();
      this.updateDropzoneCounts();
      return;
    }

    const match = this.currentRules.presets.find(entry => entry && entry.name === name);
    if (!match) {
      this.charmPresetLimits = null;
      this.updateCharmStatus();
      this.updateDropzoneLimits();
      this.updateDropzoneCounts();
      return;
    }

    this.charmPresetLimits = {
      big: Number(match.big || 0),
      small: Number(match.small || 0)
    };
    this.updateDropzoneLimits();
    this.updateDropzoneCounts();
    this.updateCharmStatus();
  }

  isCharmSelectionComplete() {
    if (!this.requiresBeadSelection || !this.charmPresetLimits || !this.charmSelection) {
      return true;
    }

    const requiredBig = this.charmPresetLimits.big || 0;
    const requiredSmall = this.charmPresetLimits.small || 0;
    const selectedBig = this.charmSelection.totals ? (this.charmSelection.totals.big || 0) : 0;
    const selectedSmall = this.charmSelection.totals ? (this.charmSelection.totals.small || 0) : 0;

    return selectedBig >= requiredBig && selectedSmall >= requiredSmall;
  }

  calculateCharmSurcharge() {
    if (!this.requiresBeadSelection || !this.currentRules || !this.charmPresetLimits) {
      return 0;
    }

    const addonPricing = this.currentRules.addonPricing || {};
    const bigPrice = Number(addonPricing.big || 0);
    const smallPrice = Number(addonPricing.small || 0);

    const selectedBig = this.charmSelection.totals.big || 0;
    const selectedSmall = this.charmSelection.totals.small || 0;

    return (selectedBig * bigPrice) + (selectedSmall * smallPrice);
  }

  updateAddToCartState(enabled) {
    const submitButton = this.activeForm ? this.activeForm.querySelector('.button.button--primary') : null;
    if (!submitButton) {
      return;
    }
    if (enabled) {
      submitButton.removeAttribute('disabled');
    } else {
      submitButton.setAttribute('disabled', 'disabled');
    }
  }

  interpolateTemplate(template, replacements) {
    if (!template || typeof template !== 'string' || !replacements) {
      return template || '';
    }

    let output = template;
    Object.entries(replacements).forEach(([key, value]) => {
      const stringValue = String(value);
      const patterns = [
        new RegExp(`{{\\s*${key}\\s*}}`, 'g'),
        new RegExp(`%${key}%`, 'g')
      ];
      patterns.forEach(pattern => {
        output = output.replace(pattern, stringValue);
      });
    });
    return output;
  }

  parseRules(source) {
    if (!source) {
      return null;
    }

    if (typeof source === 'object') {
      return source;
    }

    try {
      return JSON.parse(source);
    } catch (error) {
      return null;
    }
  }

  getColorLabel(position) {
    const template = (this.strings && this.strings.colorOption) || '';
    if (template.includes('%s')) {
      return template.replace('%s', position);
    }
    if (template.includes('{{n}}')) {
      return template.replace('{{n}}', position);
    }
    return `Color option ${position}`;
  }

  normalizeColorToken(token) {
    const raw = String(token || '').trim();
    if (!raw) {
      return {
        token: '',
        label: '',
        hex: '#CCCCCC'
      };
    }

    let label = raw;
    let reference = raw;

    const separatorMatch = raw.match(/^(.*?)[|:](.+)$/);
    if (separatorMatch) {
      if (separatorMatch[1].trim()) {
        label = separatorMatch[1].trim();
      }
      if (separatorMatch[2].trim()) {
        reference = separatorMatch[2].trim();
      }
    }

    const hexMatch = raw.match(/#([0-9a-f]{3,8})/i);
    if (hexMatch) {
      reference = `#${hexMatch[1]}`;
      if (!separatorMatch) {
        const prefix = raw.slice(0, raw.indexOf(hexMatch[0])).replace(/[()]/g, '').trim();
        if (prefix) {
          label = prefix;
        }
      }
    }

    label = label.replace(/\s*\(\s*\)\s*$/, '').trim();
    if (!label) {
      label = raw;
    }

    const hex = this.getColorHex(reference || label);

    return {
      token: raw,
      label,
      hex: hex || '#CCCCCC'
    };
  }

  getColorHex(colorName) {
    const value = String(colorName || '').trim();
    if (!value) {
      return '#CCCCCC';
    }

    if (/^rgba?\(/i.test(value)) {
      return value;
    }

    const hexMatch = value.match(/#([0-9a-f]{3,8})/i);
    if (hexMatch) {
      let hex = `#${hexMatch[1]}`;
      if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      } else if (hex.length === 5) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      } else if (hex.length === 9) {
        hex = `#${hex.substring(1, 7)}`;
      } else {
        hex = `#${hex.substring(1, 7)}`;
      }
      return hex.toUpperCase();
    }

    const pairSeparators = ['|', ':', ';', ','];
    for (const sep of pairSeparators) {
      if (value.includes(sep)) {
        const parts = value.split(sep);
        if (parts.length >= 2) {
          const candidate = this.getColorHex(parts[1]);
          if (candidate && candidate !== '#CCCCCC') {
            return candidate;
          }
        }
      }
    }

    if (/^[0-9a-f]{3}$/i.test(value)) {
      return `#${value.split('').map(char => char + char).join('').toUpperCase()}`;
    }
    if (/^[0-9a-f]{6}$/i.test(value)) {
      return `#${value.toUpperCase()}`;
    }
    if (/^[0-9a-f]{8}$/i.test(value)) {
      return `#${value.substring(0, 6).toUpperCase()}`;
    }

    const key = value.toLowerCase().trim();
    const normalizedKey = key.replace(/[_-]+/g, ' ');

    const map = {
      red: '#FF0000',
      blue: '#0000FF',
      green: '#00FF00',
      yellow: '#FFFF00',
      pink: '#FF3DEE',
      purple: '#800080',
      orange: '#FFA500',
      black: '#000000',
      white: '#FFFFFF',
      gray: '#808080',
      grey: '#808080',
      brown: '#8B4513',
      beige: '#F5F5DC',
      silver: '#C0C0C0',
      gold: '#FFD700',
      ivory: '#FFFFF0',
      teal: '#008080',
      turquoise: '#40E0D0',
      magenta: '#FF00FF',
      lavender: '#E6E6FA',
      coral: '#FF7F50',
      'light blue': '#87CEEB',
      lightblue: '#87CEEB',
      'skyblue': '#87CEEB',
      'sky blue': '#87CEEB',
      'sky-blue': '#87CEEB',
      'sapphire blue': '#0F52BA',
      'sapphire-blue': '#0F52BA',
      'ocean blue': '#1B3B6F',
      'ocean-blue': '#1B3B6F',
      'forest green': '#228B22',
      'forest-green': '#228B22',
      'rose pink': '#F8BBD0',
      'rose-pink': '#F8BBD0',
      'powder pink': '#F6C1C9',
      'powder-pink': '#F6C1C9',
      'sunset pink': '#FF9AA2',
      'sunset-pink': '#FF9AA2',
      'mustard gold': '#D4A017',
      'mustard-gold': '#D4A017',
      'golden yellow': '#FFC107',
      'golden-yellow': '#FFC107',
      'sunbeam yellow': '#FFE066',
      'sunbeam-yellow': '#FFE066',
      'natural beige': '#E6D3B3',
      'natural-beige': '#E6D3B3',
      'soft taupe': '#D2B1A3',
      'soft-taupe': '#D2B1A3',
      'mint green': '#98FF98',
      'mint-green': '#98FF98',
      'navy': '#000080',
      'navy blue': '#000080',
      'navy-blue': '#000080',
      cream: '#FFFDD0',
      'cream white': '#FFFDD0'
    };

    if (map[key]) {
      return map[key];
    }
    if (map[normalizedKey]) {
      return map[normalizedKey];
    }

    return '#CCCCCC';
  }

  formatMoney(cents) {
    return `€${(cents / 100).toFixed(2)}`;
  }

  parseBeadInventory(source) {
    if (!source) {
      return {};
    }

    if (typeof source === 'object' && !Array.isArray(source)) {
      return source;
    }

    if (typeof source === 'string') {
      try {
        return JSON.parse(source);
      } catch (error) {
        return {};
      }
    }

    return {};
  }

  getBeadInventory(name) {
    if (!name || !this.currentBeadInventory) {
      return null;
    }

    const inventory = this.currentBeadInventory[name];
    return inventory !== undefined ? Number(inventory) : null;
  }

  isBeadInStock(name) {
    const inventory = this.getBeadInventory(name);
    if (inventory === null) {
      return true;
    }
    return inventory > 0;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomBuilder();
  });
} else {
  new CustomBuilder();
}
